import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import AuthLayout from '../components/layout/AuthLayout';
import Input, { EmailIcon, LockIcon } from '../components/ui/Input';
import Button, { GoogleIcon, MicrosoftIcon } from '../components/ui/Button';
import { authService } from '../services/authService';
import './Auth.css';

const Login = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [requiresMfa, setRequiresMfa] = useState(false);

  const closeError = () => {
    setErrors({});
  };

  const closeSuccess = () => {
    setSuccessMessage('');
  };

  // Check for success messages from URL parameters
  useEffect(() => {
    const registered = searchParams.get('registered');
    const verified = searchParams.get('verified');
    
    if (registered === 'true') {
      setSuccessMessage('Registration successful! Please check your email to verify your account, then login.');
      // Clear the URL parameter after showing the message
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    } else if (verified === 'true') {
      setSuccessMessage('Email verified successfully! You can now login to your account.');
      // Clear the URL parameter after showing the message
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      handleLogin();
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    return newErrors;
  };

  const handleLogin = async () => {
    // Clear any existing success messages when attempting login
    setSuccessMessage('');
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    setErrors({}); // Clear any previous errors
    
    try {
      const response = await authService.login(formData);
      console.log('Login successful:', response);

      if (response?.requiresMfa && response?.mfaToken) {
        setRequiresMfa(true);
        setMfaToken(response.mfaToken);
        setSuccessMessage(response.message || 'Enter the MFA code sent to your email.');
        return;
      }
      
      // Verify we have the required data (check for both token formats)
      if (!response || (!response.token && !response.access_token && !response.success && !response.status)) {
        throw new Error('Invalid response from server. Please try again.');
      }
      
      // Clear any existing error messages
      setErrors({});
      
      // Small delay to show success state before redirect
      setTimeout(() => {
        navigate('/dashboard');
      }, 100);
      
    } catch (error) {
      console.error('Login failed:', error);
      
      // Handle different types of errors
      let errorMessage = 'Login failed. Please try again.';
      let showResendLink = false;
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 401) {
          // Check if it's an email verification error
          if (data && data.message && data.message.toLowerCase().includes('verify your email')) {
            errorMessage = 'Please verify your email before logging in.';
            showResendLink = true;
          } else {
            errorMessage = 'Invalid email or password. Please check your credentials.';
          }
        } else if (status === 429) {
          errorMessage = 'Too many login attempts. Please try again later.';
        } else if (status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (data && data.message) {
          errorMessage = data.message;
          // Also check the message for verification requirement
          if (data.message.toLowerCase().includes('verify your email')) {
            showResendLink = true;
          }
        }
      } else if (error.message) {
        // Network or other errors
        if (error.message.includes('Failed to connect')) {
          errorMessage = 'Cannot connect to server. Please check your internet connection.';
        } else if (error.message.includes('Network Error')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          errorMessage = error.message;
          // Also check the message for verification requirement
          if (error.message.toLowerCase().includes('verify your email')) {
            showResendLink = true;
          }
        }
      }
      
      setErrors({ 
        general: errorMessage,
        needsVerification: showResendLink
      });
    } finally {
      // Always clear loading state
      setLoading(false);
    }
  };

  const handleVerifyMfa = async () => {
    if (!otpCode || otpCode.length < 4) {
      setErrors({ general: 'Enter the 6-digit code from your email.' });
      return;
    }
    setLoading(true);
    setErrors({});
    try {
      const response = await authService.verifyMfa({ mfaToken, otpCode });
      if (!response?.access_token && !response?.token) {
        throw new Error('MFA verification failed');
      }
      navigate('/dashboard');
    } catch (error) {
      setErrors({
        general: error.response?.data?.message || error.message || 'Invalid MFA code',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    setErrors({
      general: `${provider === 'google' ? 'Google' : 'Microsoft'} sign-in is coming soon. Please use email and password for now.`,
    });
  };

  return (
    <AuthLayout>
      <div className="auth-form">
        <div className="auth-header">
          <h1 className="auth-title">Sign in to Peleescribe.ai</h1>
        </div>

        <div className="auth-content">
          {/* Social Login Buttons — OAuth backend planned for a later stage */}
          <div className="social-login">
            <div className="social-buttons">
              <Button
                variant="secondary"
                className="btn-google"
                icon={GoogleIcon}
                onClick={() => handleSocialLogin('google')}
                type="button"
              >
                Google
              </Button>
              <Button
                variant="secondary"
                className="btn-microsoft"
                icon={MicrosoftIcon}
                onClick={() => handleSocialLogin('microsoft')}
                type="button"
              >
                Microsoft
              </Button>
            </div>
            <div className="divider">
              <span className="divider-text">Or</span>
            </div>
          </div>

          {/* Login Form */}
          <div className="auth-form-fields">
            {successMessage && (
              <div className="success-message general-success">
                <span className="message-text">{successMessage}</span>
                <button 
                  type="button"
                  className="message-close-btn"
                  onClick={closeSuccess}
                  aria-label="Close success message"
                >
                  ×
                </button>
              </div>
            )}
            
            {errors.general && (
              <div className="error-message general-error">
                <span className="message-text">
                  {errors.general}
                  {errors.needsVerification && (
                    <>
                      {' '}
                      <Link to="/resend-verification" style={{ color: 'inherit', textDecoration: 'underline' }}>
                        Resend verification email
                      </Link>
                    </>
                  )}
                </span>
                <button 
                  type="button"
                  className="message-close-btn"
                  onClick={closeError}
                  aria-label="Close error message"
                >
                  ×
                </button>
              </div>
            )}

            {requiresMfa ? (
              <>
                <div className="form-group">
                  <label className="form-label">MFA code</label>
                  <Input
                    type="text"
                    name="otpCode"
                    placeholder="6-digit code from email"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    icon={LockIcon}
                  />
                </div>
                <div className="form-actions">
                  <Button
                    type="button"
                    variant="primary"
                    size="large"
                    loading={loading}
                    disabled={loading}
                    className="submit-button"
                    onClick={handleVerifyMfa}
                  >
                    Verify &amp; Sign In
                  </Button>
                </div>
              </>
            ) : (
              <>
            <div className="form-group">
              <label className="form-label">Email address</label>
              <Input
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                icon={EmailIcon}
                error={errors.email}
              />
            </div>

            <div className="form-group">
            <label className="form-label">Password</label>
              <Input
                type="password"
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                icon={LockIcon}
                showPasswordToggle
                error={errors.password}
              />
              <Link to="/forgot-password" className="forgot-password-link">
                Forget Password
              </Link>
            </div>

            <div className="form-actions">
              <Button
                type="button"
                variant="primary"
                size="large"
                loading={loading}
                disabled={loading}
                className="submit-button"
                onClick={handleLogin}
              >
                Sign In
              </Button>
              
              <div className="auth-footer">
                <span className="auth-footer-text">Don't have an account?</span>
                <Link to="/register" className="auth-footer-link">
                  Signup
                </Link>
              </div>
            </div>
              </>
            )}
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Login;
